import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSubagents } from "@/hooks/use-agents";
import { useTenant } from "@/context/tenant-context";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { agentRoleLabel } from "@/lib/agent-display";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, Layers, Trash2, Plus } from "lucide-react";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  running: "success",
  idle: "secondary",
  error: "danger",
};

export function AgentSubagentsTab({
  parentAgentId,
  onOpenAgent,
  onCreateSubagent,
}: {
  parentAgentId: string;
  /** Embedded hosts (settings dialog, agent drawer) open the agent in place. */
  onOpenAgent?: (agentId: string) => void;
  /** Embedded hosts open the sub-agent wizard in place. */
  onCreateSubagent?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: subagents = [], isLoading } = useSubagents(parentAgentId);

  // Same pattern as the Agents list: use the host's callback when there is
  // one, so the popup and drawer don't navigate away to the full page.
  const openAgent = (agentId: string) => {
    if (onOpenAgent) onOpenAgent(agentId);
    else navigate(`/tenants/${tenantId}/agents/${agentId}`);
  };
  const startCreate = () => {
    if (onCreateSubagent) onCreateSubagent();
    else navigate(`/tenants/${tenantId}/agents/${parentAgentId}/subagents/new`);
  };

  const removeMutation = useMutation({
    mutationFn: (subagentId: string) => agentsApi.removeSubagent(parentAgentId, subagentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.agents.detail(parentAgentId), "subagents"] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-text-muted">Loading subagents...</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">Subagents</h2>
          <p className="text-xs text-text-muted mt-0.5">
            Subagents share this agent's VM — no additional infrastructure cost
          </p>
        </div>
        <Button
          size="sm"
          onClick={startCreate}
        >
          <Plus size={14} className="mr-1.5" />
          Add Subagent
        </Button>
      </div>

      {subagents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Layers size={36} className="mb-3 text-text-muted" />
          <p className="text-sm text-text-secondary mb-1">No subagents yet</p>
          <p className="text-xs text-text-muted mb-4">
            Add subagents to run multiple personas on this VM without extra cost
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={startCreate}
          >
            <Plus size={14} className="mr-1.5" />
            Add First Subagent
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {subagents.map((sub) => {
            const model = sub.adapterConfig?.model as string | undefined;
            return (
              <Card
                key={sub.id}
                className="cursor-pointer transition-colors hover:border-accent/40"
                onClick={() => openAgent(sub.id)}
              >
                <CardContent className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
                      <Bot size={18} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{sub.name}</p>
                      <p className="text-xs text-text-muted">
                        <span>{agentRoleLabel(sub)}</span>
                        {model && <span> · {model}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant[sub.status] ?? "default"}>
                      {sub.status}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-text-muted hover:text-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Remove subagent "${sub.name}"? This will delete its workspace from the VM.`)) {
                          removeMutation.mutate(sub.id);
                        }
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
